/**
 * AppMercureSubscriber Component
 *
 * Orchestrates all app-wide Mercure subscriptions from config
 * Handles topic resolution, error handling with retry, and logging
 */

import { useAuthStore } from "@lib/auth/authStore";
import useMercureContext from "@lib/hooks/useMercureContext";
import useToast from "@lib/hooks/useToast";
import { resolveTopic } from "@lib/utils/mercure/resolvers";
import {
  logBatchSubscribe,
  logBatchUnsubscribe,
  logError,
  logReady,
} from "@lib/utils/mercureLogger";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

const RETRY_CONFIG = {
  maxAttempts: 3,
  baseDelay: 1000,
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const retryWithBackoff = async (fn, config = RETRY_CONFIG) => {
  let lastError;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < config.maxAttempts) {
        const delay = config.baseDelay * 2 ** (attempt - 1);
        await sleep(delay);
      }
    }
  }

  throw lastError;
};

export function AppMercureSubscriber({ subscriptions, children }) {
  const client = useMercureContext();
  const userId = useAuthStore((state) => state.userId);
  const queryClient = useQueryClient();
  const toast = useToast();
  const logout = useAuthStore((state) => state.logout);

  const [isReady, setIsReady] = useState(false);

  const resolvedSubscriptions = useMemo(() => {
    if (!userId) return [];
    return subscriptions.map((sub) => ({
      ...sub,
      resolvedTopic: resolveTopic(sub.topic, { userId }),
    }));
  }, [subscriptions, userId]);

  const handleEvent = useCallback(
    async (data, subscription) => {
      const deps = {
        client,
        userId,
        data, // useMercure already passes parsed data, not event object
        queryClient,
        toast,
        logout,
      };

      try {
        await subscription.handler(deps);
      } catch (error) {
        logError(subscription.id, error, userId);

        try {
          await retryWithBackoff(() => subscription.handler(deps), RETRY_CONFIG);
        } catch (retryError) {
          console.warn(`[Mercure] Handler "${subscription.id}" failed after retries, deferring`, {
            data,
            error: retryError,
          });
        }
      }
    },
    [client, userId, queryClient, toast, logout],
  );

  useEffect(() => {
    if (!userId || !client || resolvedSubscriptions.length === 0) return;

    const subscriptionsData = resolvedSubscriptions.map((sub) => ({
      ...sub,
      wrappedHandler: (event) => handleEvent(event, sub),
    }));

    subscriptionsData.forEach(({ resolvedTopic, wrappedHandler }) => {
      client.subscribe(resolvedTopic, wrappedHandler);
    });

    const topics = subscriptionsData.map((s) => s.resolvedTopic);
    logBatchSubscribe(topics, userId);
    setIsReady(true);

    return () => {
      subscriptionsData.forEach(({ resolvedTopic, wrappedHandler }) => {
        client.unsubscribe(resolvedTopic, wrappedHandler);
      });
      logBatchUnsubscribe(topics.length, userId);
      setIsReady(false);
    };
  }, [userId, client, resolvedSubscriptions, handleEvent]);

  useEffect(() => {
    if (isReady && resolvedSubscriptions.length > 0) {
      logReady(resolvedSubscriptions.length, userId);
    }
  }, [isReady, resolvedSubscriptions.length, userId]);

  return children;
}

export default AppMercureSubscriber;
